import { useState } from "react";
import type {
  RecurrenceRule,
  TaskUrgency,
  TaskImportance,
  DayOfWeek,
  BusinessDayAdjustment,
  ITag,
} from "../../types";

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

interface RecurringTaskFormProps {
  tags: ITag[];
  onSubmit: (data: {
    title: string;
    description?: string;
    urgency: TaskUrgency;
    importance: TaskImportance;
    recurrence_rule: RecurrenceRule;
    tag_id?: string;
  }) => Promise<boolean>;
  onCancel: () => void;
}

export default function RecurringTaskForm({ tags, onSubmit, onCancel }: RecurringTaskFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState<TaskUrgency>("medium");
  const [importance, setImportance] = useState<TaskImportance>("medium");
  const [ruleType, setRuleType] = useState<"daily" | "weekly" | "monthly">("weekly");
  const [interval, setInterval] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>(["mon", "wed", "fri"]);
  const [dayOfMonth, setDayOfMonth] = useState<number | "last">(1);
  const [businessDayAdj, setBusinessDayAdj] = useState<BusinessDayAdjustment>("none");
  const [tagId, setTagId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const buildRule = (): RecurrenceRule => {
    switch (ruleType) {
      case "daily":
        return { type: "daily", interval, business_day_adjustment: businessDayAdj };
      case "weekly":
        return { type: "weekly", interval, days_of_week: daysOfWeek };
      case "monthly":
        return {
          type: "monthly",
          interval,
          day_of_month: dayOfMonth,
          business_day_adjustment: businessDayAdj,
        };
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setIsSubmitting(true);
    setCreateError(null);

    const success = await onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      urgency,
      importance,
      recurrence_rule: buildRule(),
      ...(tagId ? { tag_id: tagId } : {}),
    });

    if (!success) {
      setCreateError("Failed to create recurring task. Please try again.");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setTitle("");
    setDescription("");
    setUrgency("medium");
    setImportance("medium");
    setRuleType("weekly");
    setInterval(1);
    setDaysOfWeek(["mon", "wed", "fri"]);
    setDayOfMonth(1);
    setBusinessDayAdj("none");
    setTagId(null);
    setCreateError(null);
    onCancel();
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <h3 className="font-medium text-foreground">New Recurring Task</h3>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title"
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <div className="grid grid-cols-2 gap-3">
        <select
          value={urgency}
          onChange={(e) => setUrgency(e.target.value as TaskUrgency)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="low">Low urgency</option>
          <option value="medium">Medium urgency</option>
          <option value="high">High urgency</option>
        </select>
        <select
          value={importance}
          onChange={(e) => setImportance(e.target.value as TaskImportance)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="low">Low importance</option>
          <option value="medium">Medium importance</option>
          <option value="high">High importance</option>
        </select>
      </div>

      {/* Tag */}
      {tags.length > 0 && (
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Tag
          </label>
          <select
            value={tagId || ""}
            onChange={(e) => setTagId(e.target.value || null)}
            className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">None</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Schedule builder */}
      <div className="space-y-3">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Schedule
        </label>

        <div className="flex gap-2">
          {(["daily", "weekly", "monthly"] as const).map((type) => (
            <button
              key={type}
              onClick={() => setRuleType(type)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                ruleType === type
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Every</span>
          <input
            type="number"
            value={interval}
            onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
            min={1}
            className="w-16 rounded-lg border border-input bg-background px-2 py-1.5 text-sm text-center"
          />
          <span className="text-sm text-muted-foreground">
            {ruleType === "daily"
              ? "day(s)"
              : ruleType === "weekly"
                ? "week(s)"
                : "month(s)"}
          </span>
        </div>

        {ruleType === "weekly" && (
          <div className="flex gap-1.5 flex-wrap">
            {DAYS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() =>
                  setDaysOfWeek((prev) =>
                    prev.includes(value)
                      ? prev.filter((d) => d !== value)
                      : [...prev, value]
                  )
                }
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  daysOfWeek.includes(value)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {ruleType === "monthly" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">On day</span>
              <select
                value={dayOfMonth === "last" ? "last" : dayOfMonth}
                onChange={(e) =>
                  setDayOfMonth(
                    e.target.value === "last"
                      ? "last"
                      : parseInt(e.target.value)
                  )
                }
                className="rounded-lg border border-input bg-background px-2 py-1.5 text-sm"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
                <option value="last">Last day</option>
              </select>
            </div>
          </div>
        )}

        {(ruleType === "daily" || ruleType === "monthly") && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Business day adjustment
            </span>
            <select
              value={businessDayAdj}
              onChange={(e) =>
                setBusinessDayAdj(e.target.value as BusinessDayAdjustment)
              }
              className="rounded-lg border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="none">None</option>
              <option value="prev">Previous business day</option>
              <option value="next">Next business day</option>
            </select>
          </div>
        )}
      </div>

      {createError && (
        <p className="text-sm text-destructive">{createError}</p>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleCreate}
          disabled={!title.trim() || isSubmitting}
          className="btn-primary flex-1"
        >
          Create
        </button>
        <button onClick={handleCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  );
}
